import * as _ from "lodash-es";
import {Class} from "@/lib/legacyApi/fetchClassesForCourseByCampusId";
import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import moment from "moment-timezone";
import {LegacyDate} from "@/shared/legacyTypes/fetchClassesTypes";

const dateOptions = {
	month: "short",
	day: "numeric",
	year: "numeric",
} as const;

/**
 * Parse a date string from the API and format it as "Jan. 1, 2024"
 */
const formatDateString = (dateStr: string) => {
	const date = new Date(dateStr);
	const dateString = date.toLocaleDateString("en-US", dateOptions);
	// Add a period after the month
	const dateStringParts = dateString.split(" ");
	dateStringParts[0] = dateStringParts[0] + ".";
	return dateStringParts.join(" ");
};

const formatDaysOfWeek = (daysOfWeek: string[]) => {
	if (daysOfWeek.length === 5) {
		return "Mon–Fri";
	}
	return daysOfWeek.map(_.capitalize).join(", ");
};

/**
 * Takes a LegacyDate and loads it into a moment using its timezone
 */
export const legacyDateToMoment = (legacyDate: LegacyDate) =>
	moment.tz(legacyDate.date, legacyDate.timezone);

/**
 * Format the runtime of the class from the start and end in the campus' timezone.
 * This assumes that every class starts and ends at the same times every lesson
 */
const formatTime = (startDate: LegacyDate, endDate: LegacyDate) => {
	const startMoment = legacyDateToMoment(startDate);
	const formattedStartTime = startMoment.format("h:mm");

	const endMoment = legacyDateToMoment(endDate);
	const formattedEndTime = endMoment.format("h:mma z");

	// Change EST/EDT to ET since moment doesn't let you do that
	const formattedEndTimeNoDST = formattedEndTime.replace(/[DS]T$/, "T");

	const formattedTime = `${formattedStartTime} - ${formattedEndTimeNoDST}`;
	return formattedTime;
};

interface ClassScheduleProps {
	class: Class;
	showDayOfWeek?: boolean;
}

const ClassSchedule: FCWithHTMLAttributes<ClassScheduleProps> = ({
	class: myClass,
	showDayOfWeek = false,
	className,
}) => {
	const startDate = formatDateString(myClass.start.date);
	const endDate = formatDateString(myClass.end.date);
	const dayOfWeek = showDayOfWeek ? formatDaysOfWeek(myClass.dow) : "";
	const scheduleTime = formatTime(
		myClass.start_in_campus_timezone,
		myClass.end_in_campus_timezone
	);
	return (
		<div className={className}>
			<div>
				{startDate} – {endDate}
			</div>
			<div>
				{dayOfWeek} {scheduleTime}
			</div>
		</div>
	);
};

export default ClassSchedule;
